/**
 * `DeploymentConfigModel.ts` — same-origin HTTP client for the deployment's own
 * agent configuration. See {@link IDeploymentConfigModel} for what lives here
 * and why it is not customer data.
 *
 * Headless and dependency-light: one `fetch` per call, no SDK, no credentials
 * of its own. The server half is `deploy/config-service/` behind an nginx
 * `location`, and `campfireConfigStorePlugin` in `vite.config.ts` serves the
 * same two verbs from a local directory in dev and preview.
 */

import type { IAuthModel } from "@models/auth/IAuthModel";
import { deploymentChannel } from "@core/publicUrl";
import type { DeploymentConfigDocument, IDeploymentConfigModel } from "./IDeploymentConfigModel";

/**
 * ORIGIN-ABSOLUTE, and it must stay that way. This addresses an nginx
 * `location` at the root of a server block shared by every deployment on the
 * host — the same rule the session WebSocket, the AI proxy and
 * `/deployments.json` follow. Running it through `publicUrl()` would send a
 * channel's requests to `/jay-testing/__campfire_config/…`, which nginx does not
 * proxy and would answer with the SPA fallback: `index.html` at 200.
 */
const K_ROUTE_PREFIX = "/__campfire_config";

/** What the service calls the default deployment; mirrors `agentPromptsDeploymentKey`. */
const K_DEFAULT_DEPLOYMENT = "default";

/**
 * Long enough for a cold host, short enough that a wedged request does not hold
 * up the boot path — both callers are awaited before the catalogs are built.
 */
const K_TIMEOUT_MS = 15_000;

export class DeploymentConfigModel implements IDeploymentConfigModel {
  private readonly m_auth: IAuthModel;

  constructor(auth: IAuthModel) {
    this.m_auth = auth;
  }

  async getAsync(
    document: DeploymentConfigDocument,
    signal?: AbortSignal,
  ): Promise<string | null> {
    const response = await this.fetchWithTimeout(this.urlFor(document), { method: "GET" }, signal);
    // 404 is "never written", which is a normal state and NOT a failure — see
    // the interface note on why the two must stay distinguishable.
    if (response.status === 404) return null;
    if (!response.ok) {
      throw new Error(`[DeploymentConfigModel] GET ${document} failed: HTTP ${response.status}`);
    }

    const text = await response.text();
    // The dev/preview stand-in and nginx both answer an unknown path with
    // whatever their fallback is, and on the deploy host that fallback is
    // `index.html` at 200 — the trap `DeploymentsModel` documents. An HTML body
    // is a misconfiguration, not a document, and must not be parsed as one.
    if (text.trimStart().startsWith("<")) {
      throw new Error(
        `[DeploymentConfigModel] GET ${document} returned HTML — the config service is not reachable at ${K_ROUTE_PREFIX}.`,
      );
    }
    return text;
  }

  async putAsync(
    document: DeploymentConfigDocument,
    text: string,
    signal?: AbortSignal,
  ): Promise<void> {
    // The RAW id token, no `Bearer` prefix issue: the host expects
    // `Authorization: Bearer <token>`. `HttpAdminStorageModel` sends the same
    // token bare to AppSync because that API wants it that way; this one is an
    // ordinary bearer scheme.
    const idToken = await this.m_auth.getIdToken();
    if (!idToken) {
      throw new Error("[DeploymentConfigModel] Not signed in — deployment config cannot be written.");
    }

    const response = await this.fetchWithTimeout(
      this.urlFor(document),
      {
        method: "PUT",
        headers: { authorization: `Bearer ${idToken}`, "content-type": "application/json" },
        body: text,
      },
      signal,
    );
    if (response.status === 401 || response.status === 403) {
      throw new Error(
        `[DeploymentConfigModel] Refused: this account may not publish ${document} on this deployment.`,
      );
    }
    if (!response.ok) {
      throw new Error(`[DeploymentConfigModel] PUT ${document} failed: HTTP ${response.status}`);
    }
  }

  private urlFor(document: DeploymentConfigDocument): string {
    // `deploymentChannel()` is derived from Vite's `base`, so a build that
    // serves its assets from `/jay-testing/` IS the `jay-testing` deployment —
    // one source of truth for which config set is ours.
    const deployment = deploymentChannel() || K_DEFAULT_DEPLOYMENT;
    return `${K_ROUTE_PREFIX}/${deployment}/${document}`;
  }

  /**
   * A caller's own `signal` still wins; this only adds a ceiling. Composed with
   * `AbortSignal.any` rather than by passing the caller's signal through, so a
   * timeout does not leave the caller's controller looking aborted.
   */
  private async fetchWithTimeout(
    url: string,
    init: RequestInit,
    signal?: AbortSignal,
  ): Promise<Response> {
    const timeout = AbortSignal.timeout(K_TIMEOUT_MS);
    const composed = signal ? AbortSignal.any([signal, timeout]) : timeout;
    return await fetch(url, { ...init, signal: composed, cache: "no-store" });
  }
}
